import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { Field, inputClass } from '../../../../../components/ui/Field';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { putawayRow } from '../../../../../features/putaway/putawayThunks';
import {
  PUTAWAY_TAB,
  orderedDestinations,
  putawayDoneMessage,
  putawayProblem,
  reasonsFor,
  resolveDestination,
} from '../../../../../features/putaway/putawayUtils';
import { formatQuantity, zoneTypeLabel } from '../../../../../utils/inventoryLabels';

/**
 * Picks where a pending row goes, and how much of it.
 *
 * The destination can be chosen from the list — the suggested ones first, each
 * with the server's reasons — or **typed or pasted as a code**, which is the
 * scanner's path: the code arrives as text, it has to match a location whole,
 * and Enter confirms without a click.
 *
 * @param {object} props
 * @param {object|null} props.row - The pending row; `null` keeps it closed.
 * @param {object} props.warehouse - The warehouse in use.
 * @param {Array<object>} props.destinations - Active storage and picking locations.
 * @param {() => void} props.onClose
 * @param {(notice: {type: string, message: string}) => void} props.onDone
 */
export const PutawayModal = ({ row, warehouse, destinations, onClose, onDone }) => {
  const dispatch = useDispatch();
  const isSaving = useSelector((state) => Boolean(state.putaway.busyKey));

  const [quantity, setQuantity] = useState('');
  /** A location picked from the list. Typing a code lets go of it. */
  const [pickedId, setPickedId] = useState(null);
  const [typed, setTyped] = useState('');
  const [notes, setNotes] = useState('');
  const [saveError, setSaveError] = useState(null);

  const rowKey = row?.key;

  // Every opening starts on the whole row and on what the server suggests.
  useEffect(() => {
    if (!row) return;
    setQuantity(String(row.quantity));
    setPickedId(row.suggested?.slot_id ?? null);
    setTyped('');
    setNotes('');
    setSaveError(null);
    // The row object is replaced by every reload of the listing behind this
    // dialog; reseeding on that would wipe what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowKey]);

  if (!row) return null;

  const isRestock = row.source === PUTAWAY_TAB.RESTOCK;
  const max = Number(row.quantity);

  const picked = destinations.find((slot) => slot.id === pickedId) || null;
  const destination = typed.trim() ? resolveDestination(typed, destinations) : picked;
  const problem = putawayProblem({
    quantity,
    max,
    destination,
    typed,
    warehouseName: warehouse?.name,
  });

  const options = orderedDestinations(row, destinations).map((slot) => {
    const reasons = reasonsFor(row, slot.id);
    const code = slot.code && slot.code !== slot.name ? ` · ${slot.code}` : '';
    return {
      value: slot.id,
      label: `${slot.name}${code} — ${zoneTypeLabel(slot.zone_type)}${
        reasons.length > 0 ? ` · sugerida: ${reasons.join(', ')}` : ''
      }`,
      shortLabel: slot.name,
    };
  });

  const destinationHint = (() => {
    if (!destination) return 'Solo Almacenaje y Picking dejan el stock disponible para vender.';
    const reasons = reasonsFor(row, destination.id);
    return [
      zoneTypeLabel(destination.zone_type),
      reasons.length > 0 && `Sugerida: ${reasons.join(' · ')}`,
    ]
      .filter(Boolean)
      .join(' · ');
  })();

  const handleSave = async () => {
    if (problem || isSaving) return;
    setSaveError(null);

    const action = await dispatch(putawayRow({ row, slotId: destination.id, quantity, notes }));

    if (action.meta.requestStatus !== 'fulfilled') {
      setSaveError(action.payload);
      return;
    }

    // A location left over its capacity is said, not blocked.
    onDone({
      type: 'success',
      message: [putawayDoneMessage(row, quantity, destination.name), action.payload.capacityWarning]
        .filter(Boolean)
        .join(' '),
    });
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} title="Elegir destino" testId="putaway-modal">
      <p className="text-sm text-text-secondary text-center -mt-4 mb-6">
        {isRestock
          ? 'Reposición desde la mesa de preparación.'
          : 'Guardado de recepción a almacenaje.'}
      </p>

      <div className="space-y-4">
        {saveError && (
          <div
            data-testid="putaway-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm"
          >
            {saveError}
          </div>
        )}

        <div className="rounded-xl border border-border bg-ninja-gray/40 dark:bg-[#111] p-3">
          <p className="font-bold font-mono text-sm text-ninja-dark dark:text-white">
            {row.product_sku}
          </p>
          <p className="text-xs text-text-muted truncate">{row.product_name}</p>
          <p className="text-xs text-text-muted mt-1">
            {isRestock
              ? `Desde la mesa · orden #${row.order}`
              : `Desde ${row.slot_name || 'la bodega, sin ubicación'}`}
            {row.lot ? ` · lote ${row.lot}` : ''}
          </p>
        </div>

        <Field
          label={`Cantidad (máximo ${formatQuantity(max)})`}
          htmlFor="putaway-quantity"
          hint="Menos que el total guarda una parte y deja el resto en la lista."
        >
          <input
            id="putaway-quantity"
            type="number"
            min="0"
            step="any"
            data-testid="putaway-quantity"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            disabled={isSaving}
            className={inputClass}
          />
        </Field>

        <FilterableSelect
          variant="form"
          testId="putaway-destination"
          label="Ubicación destino"
          // What is typed wins over what was picked, so the list shows it too.
          value={destination?.id ?? null}
          onChange={(slotId) => {
            setPickedId(slotId);
            setTyped('');
          }}
          options={options}
          placeholder="Seleccionar..."
          searchPlaceholder="Buscar por nombre o código..."
          disabled={isSaving}
        />

        <Field
          label="O escribe / escanea su código"
          htmlFor="putaway-code"
          hint={destinationHint}
        >
          <input
            id="putaway-code"
            type="text"
            data-testid="putaway-code"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            // A scanner ends its read with Enter: that is the confirmation.
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleSave();
            }}
            placeholder="Pega o escanea el código de la ubicación"
            autoComplete="off"
            disabled={isSaving}
            className={inputClass}
          />
        </Field>

        <Field
          label="Nota"
          htmlFor="putaway-notes"
          hint="Opcional. Queda en el kardex junto al movimiento."
        >
          <input
            id="putaway-notes"
            type="text"
            data-testid="putaway-notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={isSaving}
            className={inputClass}
          />
        </Field>
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose} disabled={isSaving}>
          Cancelar
        </Button>
        {/* Disabled, it says what is missing. */}
        <Button
          variant="primary"
          loading={isSaving}
          loadingLabel="Guardando..."
          disabled={Boolean(problem)}
          onClick={handleSave}
          data-testid="putaway-save"
        >
          {problem || 'Guardar'}
        </Button>
      </div>
    </Modal>
  );
};
